import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star,
  Minus,
  Plus,
  ShoppingBag,
  Heart,
  Truck,
  ShieldCheck,
  RotateCcw,
  Check,
  ChevronRight,
} from "lucide-react";
import { fetchProduct, fetchProducts } from "../lib/api";
import { trackViewContent, trackAddToCart } from "../lib/tracking";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { toast } from "../hooks/use-toast";
import ProductCard from "../components/ProductCard";

function toEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  if (url.includes("youtube.com/embed/")) return url;
  return null; // treat as direct video file
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    setQty(1);
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        trackViewContent(p);
        return fetchProducts({ category: p.category });
      })
      .then((list) =>
        setRelated(list.filter((r) => r.id !== id).slice(0, 4))
      )
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center text-muted-foreground">
        A carregar produto...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">Produto não encontrado</h1>
        <Link to="/loja" className="text-primary underline">Voltar à loja</Link>
      </div>
    );
  }

  const discount = product.oldPrice
    ? Math.round((1 - product.price / product.oldPrice) * 100)
    : 0;

  const handleAdd = () => {
    addItem(product, qty);
    trackAddToCart(product, qty);
    toast({ title: "Adicionado ao carrinho", description: `${qty}x ${product.name}` });
  };

  const buyNow = () => {
    addItem(product, qty);
    trackAddToCart(product, qty);
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <nav className="text-sm text-muted-foreground mb-6 flex items-center flex-wrap gap-1">
        <Link to="/" className="hover:text-primary">Início</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to="/loja" className="hover:text-primary">Loja</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to={`/loja/${product.category}`} className="hover:text-primary capitalize">{product.category}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-10">
        {/* Gallery */}
        <div className="space-y-4">
        <div className="flex flex-col-reverse md:flex-row gap-4">
          <div className="flex md:flex-col gap-3">
            {product.images.map((img, i) => (
              <button
                key={img}
                onClick={() => setActiveImg(i)}
                className={`h-20 w-20 rounded-md overflow-hidden border-2 transition-colors ${
                  activeImg === i ? "border-primary" : "border-transparent"
                }`}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 relative aspect-square rounded-xl overflow-hidden bg-secondary">
            <img
              src={product.images[activeImg]}
              alt={product.name}
              className="h-full w-full object-cover"
            />
            {product.badge && (
              <span className={`absolute top-4 left-4 text-xs font-semibold px-3 py-1.5 rounded-full ${product.isNew ? "bg-foreground text-white" : "bg-primary text-primary-foreground"}`}>
                {product.badge}
              </span>
            )}
          </div>
        </div>

        {product.videoUrl && (
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            {toEmbedUrl(product.videoUrl) ? (
              <iframe
                src={toEmbedUrl(product.videoUrl)}
                title="Vídeo do produto"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={product.videoUrl} controls className="w-full h-full object-cover" />
            )}
          </div>
        )}
        </div>

        {/* Info */}
        <div>
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{product.brand}</span>
          <h1 className="font-serif text-3xl md:text-4xl mt-1 mb-3">{product.name}</h1>
          <p className="text-muted-foreground mb-4">{product.subtitle}</p>

          <div className="flex items-center gap-2 mb-6">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-primary text-primary" : "text-border"}`}
                />
              ))}
            </div>
            <span className="text-sm font-medium">{product.rating}</span>
            <span className="text-sm text-muted-foreground">· {product.reviews} avaliações</span>
          </div>

          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-3xl font-semibold">€ {product.price.toFixed(2)}</span>
            {product.oldPrice && (
              <>
                <span className="text-lg text-muted-foreground line-through">€ {product.oldPrice.toFixed(2)}</span>
                <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">poupa -{discount}%</span>
              </>
            )}
          </div>

          <p className="text-sm leading-relaxed text-muted-foreground mb-6">{product.description}</p>

          {/* Features */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
            {product.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-primary shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          {/* Stock */}
          <p className="text-sm text-green-600 mb-4 flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            Em stock — apenas {product.stock} unidades!
          </p>

          {/* Qty + add */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center border border-border rounded-full h-12">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-12 w-12 flex items-center justify-center hover:text-primary">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-medium">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="h-12 w-12 flex items-center justify-center hover:text-primary">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={handleAdd} className="flex-1 h-12 rounded-full text-base">
              <ShoppingBag className="mr-2 h-4 w-4" /> Adicionar ao carrinho
            </Button>
            <button className="h-12 w-12 rounded-full border border-border flex items-center justify-center hover:text-primary hover:border-primary transition-colors">
              <Heart className="h-5 w-5" />
            </button>
          </div>

          <Button
            onClick={buyNow}
            variant="outline"
            className="w-full h-12 rounded-full text-base border-foreground hover:bg-foreground hover:text-white"
          >
            Comprar já
          </Button>

          {/* Trust */}
          <div className="grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-border">
            {[
              { icon: Truck, text: "Envio grátis +35€" },
              { icon: ShieldCheck, text: "Garantia 2 anos" },
              { icon: RotateCcw, text: "30 dias devolução" },
            ].map((t) => (
              <div key={t.text} className="flex flex-col items-center text-center gap-2">
                <t.icon className="h-6 w-6 text-primary" />
                <span className="text-xs text-muted-foreground">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-serif text-2xl md:text-3xl mb-6">Também vais gostar</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
